/**
 * **Was ist an einem bestimmten Tag dran?** – die Frage hinter dem Daily.
 *
 * Nils, 24.08.2026: *„Da mache ich ja immer Dailys. Da weiß ich jetzt noch nicht, wo ich klicken
 * kann, dass ich die Aufgaben für beispielsweise morgen sehe. Also ich müsste in «Die Woche» auch
 * auf die Datumswerte (24.08 / 25.08) klicken können, der mir dann nach Personen gruppiert den
 * Inhalt anzeigt – jeweils mit den in Work und den planned."*
 *
 * ## Warum das hier steht und nicht im Wochenstreifen
 *
 * Der Streifen **zeichnet** die Lage, diese Datei **benennt** sie – und beide müssen dasselbe
 * sagen. Stünde die Regel im JSX, gäbe es zwei Auslegungen von „läuft an diesem Tag": eine im
 * Balken und eine im Popup. Die erste, die auseinanderläuft, merkt niemand, weil beide plausibel
 * aussehen. Dieselbe Überlegung wie bei `meintag.ts` und `schirm.ts`.
 *
 * Rein: Tagesschlüssel hinein, eine Einordnung heraus. Kein `new Date()`.
 */

/**
 * Wie eine Aufgabe zu einem Tag steht.
 *
 * | | heißt |
 * |---|---|
 * | `laeuft` | der Balken deckt diesen Tag – belegt, nicht geschätzt |
 * | `laeuft-weiter` | sie läuft **heute** und niemand hat gesagt, dass sie heute endet |
 * | `plan` | für diesen Tag vorgesehen (`zielTag`), aber noch nicht angefangen |
 */
export const TAGESLAGEN = ['laeuft', 'laeuft-weiter', 'plan'] as const
export type Tageslage = (typeof TAGESLAGEN)[number]

export const TAGESLAGE_LABELS: Record<Tageslage, string> = {
  laeuft: 'läuft',
  'laeuft-weiter': 'läuft weiter',
  plan: 'Plan',
}

/** Was der Wochenstreifen je Aufgabe weiß – mehr braucht die Einordnung nicht. */
export type Wochenaufgabe = {
  taskId: string
  /** `laeuft` = in Arbeit, `wartet` = liegt bereit. */
  art: string
  /** Erster Wechsel nach „in Arbeit" – `null` bei Wartendem. */
  startTag: string | null
  /** Ableitbarer Termin – `null` bei der Mehrheit. */
  zielTag: string | null
}

/**
 * **Die Einordnung einer Aufgabe zu einem Tag.** `null` heißt: an diesem Tag ist sie nicht dran.
 *
 * ## Die drei Regeln
 *
 * 1. **`laeuft`** – sie ist in Arbeit, hat an oder vor dem Tag begonnen, und der Tag liegt noch
 *    innerhalb dessen, was der Streifen zeichnet: bis heute, oder bis zum Zieltermin, wenn es
 *    einen gibt. Das ist die belegte Aussage.
 * 2. **`laeuft-weiter`** – sie ist **heute** in Arbeit, der gefragte Tag liegt in der Zukunft und
 *    niemand hat ein Ende genannt (Nils, 24.08.2026, auf die Frage, was an einem künftigen Tag
 *    unter „läuft" stehen soll: *„Alles, was gerade läuft"*).
 *
 *    Das ist bewusst eine **Annahme** und deshalb eine eigene Lage, keine unter `laeuft`
 *    versteckte. Der Streifen zeichnet sie nicht – er schätzt keine Dauer –, aber ein Daily
 *    fragt *„woran sitzt Christian morgen"*, und die ehrlichste Antwort darauf ist „an dem, was
 *    er heute offen hat". Sie so zu benennen kostet ein Wort und erspart die Unterstellung, es
 *    sei ein Termin.
 * 3. **`plan`** – sie wartet und ist für genau diesen Tag vorgesehen. Ein Tag Unterschied ist
 *    hier eine Aussage, kein Rundungsfehler; deshalb Gleichheit und keine Spanne.
 *
 * Eine erledigte Aufgabe taucht gar nicht erst auf – der Wochenstreifen liefert nur Offenes.
 */
export function tageslage(aufgabe: Wochenaufgabe, tag: string, heute: string): Tageslage | null {
  if (aufgabe.art === 'wartet') {
    return aufgabe.zielTag === tag ? 'plan' : null
  }

  if (aufgabe.art !== 'laeuft' || aufgabe.startTag === null) return null
  if (tag < aufgabe.startTag) return null

  /** Bis wohin der Balken belegt reicht: zum Termin, sonst bis heute. */
  const gezeichnetBis =
    aufgabe.zielTag !== null && aufgabe.zielTag > heute ? aufgabe.zielTag : heute
  if (tag <= gezeichnetBis) return 'laeuft'

  /**
   * Dahinter beginnt die Annahme – aber nur, wenn **kein** Termin genannt ist. Wer bis Freitag
   * geplant hat, arbeitet am Montag danach nicht automatisch weiter; das wäre keine Fortschreibung
   * mehr, sondern ein Widerspruch zur eigenen Planung.
   */
  return aufgabe.zielTag === null ? 'laeuft-weiter' : null
}

export type Tagesgruppe<T> = {
  personId: string
  name: string
  lage: Tageslage
  aufgaben: T[]
}

/**
 * **Ein Tag, nach Personen gruppiert** – die Form, die das Popup braucht.
 *
 * Je Person bis zu drei Gruppen, in der Reihenfolge von {@link TAGESLAGEN}: was belegt läuft,
 * was vermutlich weiterläuft, was geplant ist. Leere Gruppen entfallen, Personen ohne alles
 * ebenfalls – eine Zeile „Davin Hertz: nichts" ist keine Auskunft, sondern Rauschen.
 *
 * Die Reihenfolge der Personen bleibt, wie sie hereinkommt: der Streifen sortiert sie schon,
 * und zwei Sortierungen für dieselbe Liste sind eine zu viel.
 */
export function tagNachPersonen<T extends Wochenaufgabe>(
  personen: readonly { personId: string; name: string; aufgaben: readonly T[] }[],
  tag: string,
  heute: string,
): Tagesgruppe<T>[] {
  const gruppen: Tagesgruppe<T>[] = []

  for (const p of personen) {
    for (const lage of TAGESLAGEN) {
      const aufgaben = p.aufgaben.filter((a) => tageslage(a, tag, heute) === lage)
      if (aufgaben.length > 0) {
        gruppen.push({ personId: p.personId, name: p.name, lage, aufgaben: [...aufgaben] })
      }
    }
  }

  return gruppen
}
